import { CellSortIcon } from "../../icons/sort";
import React, { useState } from "react";

interface CalendarProps {
  onSelect: (dateTime: string) => void; // Коллбэк для передачи выбранной даты и времени
  onlyFuture?: boolean;
}

const Calendar: React.FC<CalendarProps> = ({ onSelect, onlyFuture }) => {
  const [currentDate, setCurrentDate] = useState(new Date()); // Текущая дата (месяц и год)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null); // Выбранная дата
  const [hours, setHours] = useState<string>("00"); // Часы
  const [minutes, setMinutes] = useState<string>("00"); // Минуты
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false); // Состояние выпадающего списка месяцев

  // Обработчик выбора даты
  const handleDateClick = (day: number) => {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day // Убрали прибавление 1
    );
    setSelectedDate(date);
    onSelect(`${date.toLocaleDateString("sv-SE").split("T")[0]}T${hours}:${minutes}`);
  };

  // Обработчик изменения часов
  const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (/^\d{0,2}$/.test(value)) {
      const numericValue = Number(value);
      if (numericValue >= 0 && numericValue <= 23) {
        setHours(value); // Устанавливаем значение без ведущих нулей
        if (selectedDate) {
          onSelect(
            `${selectedDate.toLocaleDateString("sv-SE").split("T")[0]}T${value.padStart(
              2,
              "0"
            )}:${minutes}`
          );
        }
      }
    }
  };

  // Обработчик изменения минут
  const handleMinutesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (/^\d{0,2}$/.test(value)) {
      const numericValue = Number(value);
      if (numericValue >= 0 && numericValue <= 59) {
        setMinutes(value); // Устанавливаем значение без ведущих нулей
        if (selectedDate) {
          onSelect(
            `${
              selectedDate.toLocaleDateString("sv-SE").split("T")[0]
            }T${hours}:${value.padStart(2, "0")}`
          );
        }
      }
    }
  };

  // Переключение на предыдущий месяц
  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  // Переключение на следующий месяц
  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  // Переключение на предыдущий год
  const handlePrevYear = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1)
    );
  };

  // Переключение на следующий год
  const handleNextYear = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1)
    );
  };

  // Обработчик выбора месяца из выпадающего списка
  const handleMonthSelect = (monthIndex: number) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), monthIndex, currentDate.getDate())
    );
    setIsMonthDropdownOpen(false); // Закрываем выпадающий список
  };

  // Получение количества дней в текущем месяце
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Получение первого дня недели для текущего месяца
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  // Генерация дней месяца
  const daysInMonth = getDaysInMonth(
    currentDate.getFullYear(),
    currentDate.getMonth()
  );
  const firstDayOfMonth = getFirstDayOfMonth(
    currentDate.getFullYear(),
    currentDate.getMonth()
  );
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Заполнение пустых ячеек для первого дня месяца
  const emptyCells = Array(
    firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1
  ).fill(null);

  // Список месяцев
  const months = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
  ];

  const now = new Date();

  return (
    <div className="calendar">
      <div className="calendar-header flex between">
        <div className="flex gap20">
          <span>
            {(selectedDate || currentDate).toLocaleString("default", { month: "long" })}{" "}
            {(selectedDate || currentDate).getFullYear()}
          </span>
          <span
            className="month-dropdown-selector"
            onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}
          >
            <CellSortIcon width={15} height={8} classNames={["blueFilled"]} />
          </span>
        </div>
        {isMonthDropdownOpen && (
          <div className="month-dropdown">
            {months.map((month, index) => (
              <div
                key={month}
                className="month-dropdown-item"
                onClick={() => handleMonthSelect(index)}
              >
                {month}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap20">
          <button type="button" onClick={handlePrevYear} className="nav-button">
            <CellSortIcon width={15} height={8} classNames={["rotate--90"]} />
          </button>
          <button type="button" onClick={handleNextYear} className="nav-button">
            <CellSortIcon width={15} height={8} classNames={["rotate-90"]} />
          </button>
        </div>
      </div>
      <div className="calendar-weekdays">
        {["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС"].map((day) => (
          <div key={day} className="weekday">
            {day}
          </div>
        ))}
      </div>
      <div className="calendar-days">
        {emptyCells.map((_, index) => (
          <div key={`empty-${index}`} className="day empty"></div>
        ))}
        {days.map((day) => (
          <div
            key={day}
            className={`day ${
              selectedDate?.getDate() === day &&
              selectedDate?.getMonth() === currentDate.getMonth() &&
              selectedDate?.getFullYear() === currentDate.getFullYear()
                ? "selected active-date" // Добавили дополнительный класс active-date
                : ""
            } ${(() => {
              const potentialDate = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                day 
              );
              const isToday = 
              potentialDate.getDate() === now.getDate() &&
              potentialDate.getMonth() === now.getMonth() &&
              potentialDate.getFullYear() === now.getFullYear();
              const isPast = onlyFuture && potentialDate < now && !isToday;
              if (onlyFuture && isPast) {
                return "disabled"
              }
              return ""
            })()}`}
            onClick={() => handleDateClick(day)}
          >
            {day}
          </div>
        ))}
      </div>
      <div className="calendar-time flex between">
        <label>Время события:</label>
        <div className="flex">
          <div className="time-input-container">
            <input
              type="text"
              value={hours}
              onChange={handleHoursChange}
              maxLength={2}
              className="time-input"
            />
            <span>:</span>
            <input
              type="text"
              value={minutes}
              onChange={handleMinutesChange}
              maxLength={2}
              className="time-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;